import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbmDataMapping, AbmEchartsConfig } from '../global/types';
import { AbmFilePaths, AbmFilePathType } from '../utils/abm-file-path';
import { AbmUtil } from '../Utils/abm-util';

export const ECHARTS_CONFIG = new InjectionToken<AbmEchartsConfig>('ABM_ECHART_CONFIG');

@Injectable({
    providedIn: 'root',
})
export class AbmConfigService {
    public config: { [name: string]: any };
    public mappings: AbmDataMapping[];
    public staticData: { [name: string]: any };
    public echartTheme: { name: string, options: any }[];

    private _dataDir = AbmFilePaths.getPath(AbmFilePathType.DATA);

    constructor(
        private http: HttpClient,
        @Inject(ECHARTS_CONFIG) public echartsConfig?: AbmEchartsConfig,
    ) { }

    // no need manual call, already done in app startup.
    public loadStaticData(): Promise<any> {
        console.log('%cABM %cInitializing configuration...', 'color:darkorange', 'color:black');
        return Promise.all([
            this._loadConfig(),
            this._loadMapping(),
            this._loadStaticData(),
            this._loadEchartTheme(),
        ]).then(() => console.log('%cABM %cconfiguration loaded.', 'color:darkorange', 'color:black'));
    }

    private _loadConfig() {
        return this.http.get('config.json').toPromise().then(data => {
            this.config = data;
        });
    }

    private _loadMapping() {
        const mappingFile = this._dataDir + 'mappings.json';
        return this.http.get<AbmDataMapping[]>(mappingFile).toPromise().then(data => {
            this.mappings = data;
        });
    }

    /**
     * 用于在启动时加载静态数据，也可以用在程序中动态加载 data 目录的数据。
     * @param fileName Optional, 位于 data path 的需加载的文件名。默认为 static-data.json
     */
    public _loadStaticData(fileName?: string) {

        const file = fileName ?? 'static-data.json';

        const staticFile = this._dataDir + file;
        return this.http.get(staticFile).toPromise().then(data => { this.staticData = data; });
    }

    private _loadEchartTheme() {
        if (this.echartsConfig?.themes) {

            let themes: string[];
            if (typeof this.echartsConfig.themes === 'string') {
                themes = [this.echartsConfig.themes];
            } else {
                themes = this.echartsConfig.themes;
            }

            const themeDir = AbmFilePaths.getPath(AbmFilePathType.ECHARTS) + 'theme/';

            return forkJoin(themes.map(theme => {
                const themeFile = themeDir + theme + '.json';
                return this.http.get<any>(themeFile).pipe(map(options => {
                    return { name: theme, options };
                }));
            })).toPromise().then(data => { this.echartTheme = data; });
        }
    }

    private _loadEchartsMapCities() { // TODO add this to load
        const cityDataFile = AbmFilePaths.getPath(AbmFilePathType.DATA) + 'echarts-city-loc.json';
        const cityGeoMap = new Map<string, [string, string]>();
        this.http.get<any>(cityDataFile).subscribe(data => {
            (data as any[]).forEach(d => {
                cityGeoMap.set(d.name, [d.log, d.lat]);
                if (d.children) {
                    d.children.forEach(c => cityGeoMap.set(c.name, [c.log, c.lat]));
                }
            });
        });
    }

    public getConfig(name: string) {
        return AbmUtil.getPropertyOrOthers<any>(this.config, name);
    }

    public getMapping(name: string): AbmDataMapping {
        return AbmUtil.findInArray<AbmDataMapping>(this.mappings, 'name', name);
    }

    public getStaticData(name: string) {
        return AbmUtil.getPropertyOrOthers<any>(this.staticData, name);
    }

    public getDataServerConfig(): { apiEnable: boolean, url: string, api: string } {
        return this.config.dataServer;
    }

    public getBeConfig(): { apiEnable: boolean, url: string, api: string } {
        return this.config.be;
    }

    public getWsConfig(): { wsEnable: boolean, url: string, ws: string } {
        return this.config.be;
    }
}
